<template>
  <div ref="productView" class="product">
    <ProductSearch class="mb-5" />

    <content-placeholders-text
      v-if="!product"
      :lines="1"
      style="margin-bottom: 3rem"
    />
    <CategoryBreadcrumb
      v-else
      :language="language"
      :productPath="productPath"
      style="margin-bottom: 3rem"
    />
    <section id="product-overview">
      <content-placeholders
        v-if="!product"
        class="product-details-container flex-col md:flex-row"
      >
        <content-placeholders-img class="h-full md:w-1/2 lg:w-1/3" />
        <div class="md:w-1/2 lg:w-2/3">
          <content-placeholders-heading />
          <content-placeholders-text />
        </div>
      </content-placeholders>

      <div v-else class="product-details-container flex-col md:flex-row">
        <ImageViewer :mime="product.mime" />
        <div class="md:w-1/2 lg:w-2/3">
          <ProductDetails
            :title="product.overallInformation?.descriptionShort"
            :description="product.overallInformation?.descriptionLong"
            :language="language"
          />

          <!-- <Socials :lang="language" /> -->
        </div>
      </div>
    </section>

    <div v-if="product" class="flex flex-col md:flex-row gap-6">
      <ProductSectionNav class="md:w-1/3 xl:w-1/5" />
      <div class="flex flex-col md:w-2/3 xl:w-4/5">
        <section id="product-specifications" class="mb-5">
          <h3>Specifications</h3>
          <hr />
          <div class="overflow-auto">
            <SpecsTable
              class="mt-4"
              :productDimensions="productDimensions.product"
              :packagingDimensions="productDimensions.packing"
            />
          </div>
          <!-- <PDFViewer src="https://drive.google.com/file/d/1a7g9xv-BVXUZbkfGhFMNFAcID1Ll1S6e/view" /> -->
        </section>

        <section id="product-video" class="my-5">
          <h3>Video</h3>
          <hr />
          <div class="py-5">
            <div v-if="youtubeFilter?.length">
              <iframe
                v-for="(video, index) in youtubeFilter"
                :key="index"
                class="w-full aspect-video"
                :src="video"
                title="Youtube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-wirte; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              >
              </iframe>
            </div>
            <span v-else><i> Product has no video </i></span>
          </div>
        </section>

        <!-- <section id="product-model">
          <h3>3D Model</h3>
          <hr />
          <View3D :src="'eb_house_plant_01.obj'" />
        </section> -->

        <section id="product-attachments" class="my-5">
          <h3>Attachments</h3>
          <hr />
          <!-- <div v-if="!attachmentsFilter?.length" class="py-5">
            <i>Product has no attachments</i>
          </div> -->
          <ul class="py-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <li>
              <a
                href="https://drive.google.com/file/d/1a7g9xv-BVXUZbkfGhFMNFAcID1Ll1S6e/view"
                target="_blank"
              >
                <div class="flex items-center gap-3 py-3 px-4 border">
                  <box-icon class="w-12 md:w-24" name="food-menu"></box-icon>
                  <h5 class="m-0">Product Catalogue</h5>
                </div>
              </a>
            </li>
            <li v-for="(attachment, index) in attachmentsFilter" :key="index">
              <a :href="attachment" class="break-all flex items-center gap-2">
                <div class="flex items-center gap-3 py-3 px-4 border">
                  <box-icon class="w-12 md:w-24" name="download"></box-icon>
                  <h5 class="m-0 line-clamp-2">{{ attachment }}</h5>
                </div>
              </a>
            </li>
          </ul>
        </section>
        <hr />
        <Button solid class="product-page-cta my-5">ASK A QUESTION</Button>
      </div>
    </div>
  </div>
</template>

<script>
import CategoryBreadcrumb from "@/components/Categories/CategoryBreadcrumb.vue";
import ProductDetails from "@/components/Products/ProductPage/ProductDetails.vue";
import ImageViewer from "@/components/Products/ProductPage/ImageViewer.vue";
import Button from "@/components/UI/Button.vue";
import ProductSearch from "@/components/Products/ProductPage/ProductSearch.vue";
import ProductSectionNav from "@/components/Products/ProductPage/ProductSectionNav.vue";
import PDFViewer from "@/components/PDFViewer.vue";
import Socials from "@/components/UI/Socials.vue";
import View3D from "@/components/View3D.vue";
import SpecsTable from "@/components/UI/SpecsTable.vue";

export default {
  name: "ProductView",
  components: {
    CategoryBreadcrumb,
    ProductDetails,
    ImageViewer,
    Button,
    ProductSearch,
    ProductSectionNav,
    PDFViewer,
    Socials,
    View3D,
    SpecsTable,
  },
  computed: {
    product() {
      return this.$store.getters.selectedProduct;
    },
    productId() {
      return this.$route.params.productId;
    },
    productPath() {
      return {
        productId: this.product?.productId,
        leaves: this.product?.categories.leaves["Rofix_Products"] || [],
        name: this.product?.overallInformation.descriptionShort || "",
      };
    },
    language() {
      return this.$route.query.lang || "en";
    },
    youtubeFilter() {
      return this.product?.mime
        ?.filter(
          (mime) =>
            mime.mimeSource?.en?.startsWith(
              "https://www.youtube.com/watch?v="
            ) ||
            mime.mimeSource?.en?.startsWith("https://www.youtube.com/embed")
        )
        .map((mime) => {
          return mime.mimeSource?.en.replace("watch?v=", "embed/");
        });
    },

    attachmentsFilter() {
      return this.product?.mime
        ?.filter((mime) => {
          const toCheck = mime.mimeFilename || mime.mimeSource || {};

          const isImages = Object.values(toCheck).some((fileName) => {
            if (
              fileName.includes(".jpeg") ||
              fileName.includes(".jpg") ||
              fileName.includes(".png")
            ) {
              return true;
            }
            return false;
          });
          if (
            mime.mimeSource?.en?.startsWith(
              "https://www.youtube.com/watch?v="
            ) ||
            mime.mimeSource?.en?.startsWith("https://www.youtube.com/embed") ||
            isImages
          ) {
            return false;
          } else {
            return true;
          }
        })
        .map((attachmentsMime) => attachmentsMime.mimeSource.en);
    },

    productDimensions() {
      const productDimensions = this.product.overallInformation;
      const packagingDimensions = this.product.packingInformation[0];
      return {
        product: {
          length: productDimensions.netLength,
          width: productDimensions.netWidth,
          depth: productDimensions.netDepth,
          volume: productDimensions.netVolume,
          weight: productDimensions.netWeight,
        },
        packing: {
          length: packagingDimensions.length,
          width: packagingDimensions.width,
          depth: packagingDimensions.depth,
          volume: packagingDimensions.volume,
          weight: packagingDimensions.weight,
        },
      };
    },
  },
  created() {
    if (!this.product) {
      this.$store
        .dispatch("fetchSingleProduct", {
          id: this.productId,
        })
        .then((product) => {
          this.$store.commit("setSelectedProduct", product);
        });
    }
  },
  mounted() {
    this.$refs.productView?.scrollIntoView();
  },
  async beforeRouteUpdate(to, from, next) {
    const nextProduct = to.params.productId;
    const prevProduct = from.params.productId;

    if (nextProduct !== prevProduct) {
      this.$store.commit("clearSelectedProduct");

      await this.$store.dispatch("fetchSingleProduct", {
        id: this.productId,
        isSelected: true,
      });
    }

    next();
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit("clearSelectedProduct");
    next();
  },
};
</script>

<style>
.product {
  width: 98%;
  margin: 0 auto;
}

.product-details-container {
  display: flex;
  gap: 3rem;
  margin-bottom: 1.4rem;
}

.product-page-cta {
  margin-bottom: 2rem;
}
</style>

<style scoped>
a {
  text-decoration: underline;
}
</style>
